import React, { useEffect, useState } from "react"
import { LoadingFlex, Stat } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import {useIntl, FormattedMessage} from "react-intl"
import messages from "./messages"

const FinancialStats = ({ customer }) => {
    const { formatMessage, formatNumber, formatDate } = useIntl()
    const [loadingFinancialStats, setLoadingFinancialStats] = useState(false)
    const [customerFinancialStats, setCustomerFinancialStats] = useState(true)

    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() // 0 = January, 5 = June, 11 = December

    // Calculate start and end of current fiscal year (July 1 - June 30)
    const fiscalCurrentYearStart = currentMonth >= 6
        ? new Date(`${currentYear}-07-01`)
        : new Date(`${currentYear - 1}-07-01`)
    const fiscalCurrentYearEnd = currentMonth >= 6
        ? new Date(`${currentYear + 1}-06-30`)
        : new Date(`${currentYear}-06-30`)

    // Calculate start and end of last fiscal year
    const fiscalLastYearStart = currentMonth >= 6
        ? new Date(`${currentYear - 1}-07-01`)
        : new Date(`${currentYear - 2}-07-01`)
    const fiscalLastYearEnd = currentMonth >= 6
        ? new Date(`${currentYear}-06-30`)
        : new Date(`${currentYear - 1}-06-30`)

    useEffect(() => {
        setLoadingFinancialStats(true)
        fetch(`/rest/v1/customer/${customer.uid}/financial-stats`)
            .then((response) => response.json())
            .then((data) => {
                setCustomerFinancialStats(data)
                setLoadingFinancialStats(false)
            })
            .catch((err) => {
                toastr.error(err)
            })
    }, [customer])

    return (
        <div className="p-4 text-center">
            {loadingFinancialStats ? (
                <LoadingFlex>
                    <div className="flex items-center space-x-1">
                        <span className="font-medium"><FormattedMessage {...messages.loading} /></span>
                        <Icon icon={["fal", "spinner-third"]} className="animate-spin" />
                    </div>
                </LoadingFlex>
            ) : (
                <div className="ml-2 text-left space-y-2">
                    <div>
                        <div className="text-bold text-xl">
                            <FormattedMessage {...messages.title} />
                        </div>
                        <div className="text-sm">
                            <FormattedMessage
                                {...messages.description}
                                values={{
                                    fiscalCurrentYear: `${formatDate(fiscalCurrentYearStart)} - ${formatDate(fiscalCurrentYearEnd)}`,
                                    fiscalLastYear: `${formatDate(fiscalLastYearStart)} - ${formatDate(fiscalLastYearEnd)}`,
                                }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3 flex-initial w-full text-left order-3 md:order-none">
                        <Stat
                            name={formatMessage(messages.revenue)}
                            icon="dollar-sign"
                            data={{
                                current: formatNumber(customerFinancialStats?.data?.revenue_ytd, { style: 'currency', currency: customerFinancialStats?.data?.revenue_ytd_currency?.iso ?? 'EUR' }),
                                currentText: formatMessage(messages.from, {
                                    fromValue: formatNumber(customerFinancialStats?.data?.revenue_last_year, { style: 'currency', currency: customerFinancialStats?.data?.revenue_last_year_currency?.iso ?? 'EUR' }),
                                }),
                                change: customerFinancialStats?.data?.revenue_last_year
                                    ? parseFloat(((customerFinancialStats?.data?.revenue_ytd - customerFinancialStats?.data?.revenue_last_year) / customerFinancialStats?.data?.revenue_last_year) * 100).toFixed(2)
                                    : 100
                            }}
                            symbol="%"
                        />
                        <Stat
                            name={formatMessage(messages.revenue_percentage)}
                            icon="percentage"
                            data={{
                                current: `${customerFinancialStats?.data?.revenue_percentage_ytd}%`,
                                currentText: formatMessage(messages.from, {
                                    fromValue: `${customerFinancialStats?.data?.revenue_percentage_last_year}%`
                                }),
                                change: customerFinancialStats?.data?.revenue_percentage_ytd - customerFinancialStats?.data?.revenue_percentage_last_year
                            }}
                            symbol="%"
                        />
                        <Stat
                            name={formatMessage(messages.gross_margin)}
                            icon="chart-line"
                            data={{
                                current: `${customerFinancialStats?.data?.gross_margin_fiscal_year}%`,
                                currentText: formatMessage(messages.from, {
                                    fromValue: `${customerFinancialStats?.data?.gross_margin_last_year}%`
                                }),
                                change: customerFinancialStats?.data?.gross_margin_fiscal_year - customerFinancialStats?.data?.gross_margin_last_year
                            }}
                            symbol="%"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3 flex-initial w-full text-left order-3 md:order-none">
                        <Stat
                            name={formatMessage(messages.growth)}
                            icon="chart-area"
                            data={{
                                current: `${customerFinancialStats?.data?.growth_rate_yoy}%`
                            }}
                            symbol="%"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default FinancialStats
